import { type ClassValue, clsx } from "clsx";
import { twMerge } from "tailwind-merge";
import type { Session } from "@supabase/supabase-js";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export function getUserDataFromSession(session: Session) {
  const userId = session.user.id;
  const userAvatarUrl = session.user.user_metadata.avatar_url;
  const username = session.user.user_metadata.user_name;

  return { userId, userAvatarUrl, username };
}

/**
 * Validates user input for a textarea field.
 *
 * @param input - The user input string to validate.
 * @param options - Optional configuration for validation.
 * @returns An object containing whether the input is valid and an error message (if any).
 */
export function validateTextareaInput(
  input: string,
  options?: {
    maxLength?: number; // Maximum allowed length of the input
    disallowedPatterns?: RegExp[]; // Patterns to disallow (e.g., `<script>`, SQL injection)
    allowEmpty?: boolean; // Whether empty input is allowed
  }
): { isValid: boolean; errorMessage?: string } {
  const defaultOptions = {
    maxLength: 500,
    disallowedPatterns: [
      /<script.*?>.*?<\/script>/gi, // Prevents embedded scripts
      /SELECT .* FROM/gi, // Blocks common SQL injection patterns
      /INSERT INTO/gi,
      /DELETE FROM/gi,
      /UPDATE .* SET/gi,
      /DROP TABLE/gi,
      /<.*?on\w+=.*?>/gi, // Blocks inline event handlers
    ],
    allowEmpty: false,
  };

  const { maxLength, disallowedPatterns, allowEmpty } = {
    ...defaultOptions,
    ...options,
  };

  // Trim the input
  const trimmedInput = input.trim();

  // Check if the input is empty
  if (!allowEmpty && trimmedInput.length === 0) {
    return { isValid: false, errorMessage: "Input cannot be empty." };
  }

  // Check the input length
  if (trimmedInput.length > maxLength) {
    return {
      isValid: false,
      errorMessage: `Input exceeds the maximum length of ${maxLength} characters.`,
    };
  }

  // Check for disallowed patterns
  for (const pattern of disallowedPatterns) {
    if (pattern.test(trimmedInput)) {
      return {
        isValid: false,
        errorMessage: "disallowed content.",
      };
    }
  }

  // If all checks pass, the input is valid
  return { isValid: true };
}

//max size in bytes
export function validateMediaInput(
  file: File,
  options: {
    maxSize: number;
    allowedTypes?: string[];
    disallowedExtensions?: string;
  }
): { isValid: boolean; errorMessage?: string } {
  // Check if the file size exceeds the max size
  if (file.size > options.maxSize) {
    return {
      isValid: false,
      errorMessage: `${file.name} size exceeds the limit of ${
        options.maxSize / 1000000
      }mb.`,
    };
  }

  // Check for dangerous file types/extensions
  const fileExtension = file.name.split(".").pop()?.toLowerCase();
  if (
    options.disallowedExtensions &&
    fileExtension &&
    options.disallowedExtensions.includes(fileExtension)
  ) {
    return {
      isValid: false,
      errorMessage: `${file.name} extension [${fileExtension}] is not allowed.`,
    };
  }

  // Check if file MIME type is allowed (optional)
  if (
    options.allowedTypes &&
    file.type &&
    !options.allowedTypes.includes(file.type)
  ) {
    return {
      isValid: false,
      errorMessage: `${file.name} type ${
        file.type
      } is not allowed. Allowed types are: ${options.allowedTypes.join(", ")}`,
    };
  }

  // Check for file size being zero (empty file)
  if (file.size === 0) {
    return { isValid: false, errorMessage: `${file.name} is empty.` };
  }

  // Block dangerous file types by MIME type (e.g., executables, scripts)
  const dangerousTypes = [
    "application/x-msdownload",
    "application/javascript",
    "text/html",
    "application/x-sh",
  ];
  if (dangerousTypes.includes(file.type)) {
    return {
      isValid: false,
      errorMessage: `${file.name} type is not allowed.`,
    };
  }
  
  return { isValid: true };
}